import { useMemo, useState } from "react";
import { KPIChartPoint } from "../types";

const WIDTH = 600;
const HEIGHT = 188;
const PAD_TOP = 16;
const PAD_BOTTOM = 26;
const PAD_X = 12;
const MAX_BAR_WIDTH = 34;
const BAR_SLOT_RATIO = 0.62;

/** Every month gets a label up to this many; beyond it they would crowd. */
const LABEL_EVERY_MONTH_UP_TO = 7;
/** Past that, every third month is labelled, and the last month always is. */
const LABEL_STEP = 3;

/**
 * How much room an axis label needs, in viewBox units, gap included. At
 * `text-[11px]` "Jul" is about twenty units wide and "Jul 2025" about fifty.
 */
const LABEL_ROOM = { month: 26, monthAndYear: 62 };

/**
 * The gap between labelled months, in columns.
 *
 * `LABEL_STEP` was sized for the thirteen-month window and held regardless of
 * length. Work item 08 raised the custom range to sixty months, where three
 * columns is 28.8 units — enough for "Jul", nowhere near enough for the year
 * a five-year axis needs to tell its five Julys apart. So the stride is
 * whatever keeps the labels from touching, and never tighter than the one the
 * thirteen-month window was drawn with.
 */
const strideFor = (length: number, labelsCarryYear: boolean): number => {
  const columnWidth = (WIDTH - PAD_X * 2) / length;
  const room = labelsCarryYear ? LABEL_ROOM.monthAndYear : LABEL_ROOM.month;

  return Math.max(LABEL_STEP, Math.ceil(room / columnWidth));
};

/**
 * Which points get an axis label.
 *
 * The thirteen-month window labels months 0, 3, 6, 9 and 12, which is every
 * third and happens to end on the last bar. A period of another length still
 * has to name its last month, since that is the headline. When the last label
 * would sit right beside the previous one, the previous one gives way.
 */
export const labelledIndexesFor = (
  length: number,
  labelsCarryYear = false,
): number[] => {
  if (length <= LABEL_EVERY_MONTH_UP_TO) {
    return Array.from({ length }, (_, index) => index);
  }

  const step = strideFor(length, labelsCarryYear);
  const indexes: number[] = [];

  for (let index = 0; index < length; index += step) {
    indexes.push(index);
  }

  const last = length - 1;
  const previous = indexes[indexes.length - 1];

  if (previous === last) {
    return indexes;
  }

  if (last - previous < step - 1) {
    indexes.pop();
  }

  indexes.push(last);

  return indexes;
};

/** A month with no revenue has no ratio, and so nothing to divide. */
export const NO_VALUE = "No revenue recorded";

export interface KPITrendChartGeometry {
  /** viewBox dimensions. The SVG scales; these are its internal units. */
  width: number;
  height: number;
  /** Vertical position of a value. */
  y: (value: number) => number;
  /** Where bars start — the padded floor of the scale, not zero. */
  baseline: number;
  barWidth: number;
  /** Centre of a month's column: bars, axis labels and the tooltip all use it. */
  centreOf: (index: number) => number;
  /** Left edge and width of a month's full-height hover column. */
  columnXOf: (index: number) => number;
  columnWidth: number;
  /** The indexes that get an axis label, clamped to the series length. */
  labelledIndexes: number[];
  /**
   * True when the window spans more than one calendar year, so an axis label
   * of "Jan" alone does not say which January it is.
   */
  labelsCarryYear: boolean;
  /** What the chart says in words, for readers who cannot see the bars. */
  summary: string;
}

/**
 * The chart's arithmetic: scale, bar geometry, and which month is hovered.
 *
 * The scale is padded below the lowest value rather than zero-based, so bars
 * read as a trend rather than as absolute magnitudes — these are ratios in a
 * narrow range, and a zero-based axis would flatten every one of them into an
 * identical block. That exaggeration is why hovering a month shows its actual
 * figures: the shape is the story, the tooltip is the evidence.
 *
 * `geometry` is null when there is nothing to plot — an empty series, or a
 * window in which every month lacked the revenue to divide by. The caller
 * renders nothing in that case, and the null is what makes that unmissable
 * rather than a chart of zero-height bars.
 */
export const useKPITrendChart = (series: KPIChartPoint[]) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const geometry = useMemo<KPITrendChartGeometry | null>(() => {
    // A month with no revenue has no ratio, so it contributes no bar and must
    // not drag the scale to zero.
    const values = series
      .map((point) => point.value)
      .filter((value): value is number => value !== null);

    if (series.length === 0 || values.length === 0) {
      return null;
    }

    const lowest = Math.min(...values);
    const highest = Math.max(...values);
    const spread = highest - lowest || 1;
    const floor = lowest - spread * 0.55;
    const ceiling = highest + spread * 0.18;

    const y = (value: number) =>
      PAD_TOP +
      (HEIGHT - PAD_TOP - PAD_BOTTOM) *
        (1 - (value - floor) / (ceiling - floor));

    const columnWidth = (WIDTH - PAD_X * 2) / series.length;
    const latest = series[series.length - 1];
    const labelsCarryYear =
      series[0].yearMonth.slice(0, 4) !== latest.yearMonth.slice(0, 4);

    return {
      width: WIDTH,
      height: HEIGHT,
      y,
      baseline: y(floor),
      barWidth: Math.min(MAX_BAR_WIDTH, columnWidth * BAR_SLOT_RATIO),
      centreOf: (index: number) => PAD_X + columnWidth * (index + 0.5),
      columnXOf: (index: number) => PAD_X + columnWidth * index,
      columnWidth,
      labelledIndexes: labelledIndexesFor(series.length, labelsCarryYear),
      labelsCarryYear,
      summary: `Monthly trend, ${series[0].monthLabel} to ${latest.monthLabel}. ${latest.monthLabel}: ${latest.valueLabel ?? NO_VALUE}.`,
    };
  }, [series]);

  return { geometry, activeIndex, setActiveIndex };
};
